'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

const ANCHOR_SELECTOR = '[data-marketing-guide-anchor]'
/** Offset (px) so the guides start below the demo frame's rounded top corner
 * instead of poking up through the curve. Matches `rounded-t-3xl` (1.5rem). */
const ANCHOR_TOP_INSET = 24

/**
 * Vertical guide lines at the `max-w-6xl` content column. The lines start
 * at the element marked with `data-marketing-guide-anchor` (the hero demo
 * frame) so they don't run above it through the hero copy, and extend to
 * the bottom of the marketing shell so they continue alongside every
 * section below.
 *
 * The inert guide node renders on every public route and its server output
 * always matches the browser's first render, so hydration stays clean. Only
 * the homepage contains `data-marketing-guide-anchor`; without that anchor
 * the node stays transparent and has no visible effect.
 *
 * It is NOT in the static shell of routes with fallback params, though:
 * `usePathname()` below hangs during the shell prerender under
 * `cacheComponents`, so `MarketingShell` renders this behind its own
 * `<Suspense fallback={null}>` and the node streams in with the resume.
 * Invisible either way (`opacity: 0` until the client measures the anchor).
 * The hook is read only inside the effect, but it has to stay a render-time
 * subscription so the offset recomputes on client-side navigation.
 */
export function MarketingGuideLines() {
  const pathname = usePathname()
  const [topOffset, setTopOffset] = useState<number | null>(null)

  useEffect(() => {
    const update = () => {
      if (pathname !== '/') {
        setTopOffset(null)
        return
      }

      const anchor = document.querySelector<HTMLElement>(ANCHOR_SELECTOR)
      const main = anchor?.closest('main')
      if (!anchor || !main) {
        setTopOffset(null)
        return
      }
      const anchorRect = anchor.getBoundingClientRect()
      const mainRect = main.getBoundingClientRect()
      setTopOffset(anchorRect.top - mainRect.top + ANCHOR_TOP_INSET)
    }

    const frameId = window.requestAnimationFrame(update)
    if (pathname !== '/') {
      return () => window.cancelAnimationFrame(frameId)
    }

    const observer = new ResizeObserver(() => update())
    if (document.body) {
      observer.observe(document.body)
    }
    window.addEventListener('resize', update)

    return () => {
      window.cancelAnimationFrame(frameId)
      observer.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [pathname])

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute bottom-0 left-1/2 z-0 hidden w-full max-w-6xl -translate-x-1/2 border-border border-x transition-opacity duration-200 lg:block"
      style={{
        top: topOffset ?? 0,
        opacity: topOffset === null ? 0 : 1,
      }}
    />
  )
}
